import { ExpenseModel, isSubmittedExpense } from "../../reduxToolkit/expensesSlice";
import { RootState } from "../../reduxToolkit/store";
import { PendingSubmissionModel, SubmissionModel, UnsavedSubmissionModel, isSubmitted } from "../../reduxToolkit/submissionsSlice";

/**
 * The expenses that belong in the given pending submission.
 *
 * Takes the membership's expenses and submissions explicitly rather than reading
 * them off RootState, so callers that already have an explicit-input shape (e.g.
 * lib/rules/incidentSubmittability.ts) can reuse it without dragging the store
 * type along. `selectPendingExpenses` below is the RootState wrapper.
 */
export function pendingExpensesFor(
  submission: PendingSubmissionModel | UnsavedSubmissionModel,
  {
    allMembershipExpenses,
    allMembershipSubmissions,
  }: {
    allMembershipExpenses: ExpenseModel[],
    allMembershipSubmissions: SubmissionModel[],
  },
): ExpenseModel[] {
  if (!submission) { return [] }

  let pendingExpenses = allMembershipExpenses
    .filter((e) =>
      e.incident_id === submission.incident_id)
    .filter((e) => !isSubmittedExpense(e))

  if (submission.submission_type === 'CHM' || submission.submission_type === 'Sedera' || submission.submission_type === 'SMI') {
    // If we're submitting to a sharing ministry (not an add-on), we need to roll up all expenses
    // that were last submitted to one of these prior submissions, as now we'll get them reimbursed
    // via the sharing ministry and repay the previous HRA submissions.
    const priorSubmissionIDs = allMembershipSubmissions
      .filter((s) => s.incident_id === submission.incident_id)
      .filter(isSubmitted)
      .map((s) => s.id)
    if (priorSubmissionIDs.length > 0) {
      pendingExpenses.push(...allMembershipExpenses
        .filter(isSubmittedExpense)
        .filter((e) => priorSubmissionIDs.includes(e.submission_id)))
    }
  } else if (submission.submission_type === 'HRA') {
    // If we're submitting to the HRA, we need to exclude expenses that were already submitted to the HRA via the card.
    pendingExpenses = pendingExpenses.filter((e) => !e.paid_with_hra)
  }

  if (
    submission.submission_type === 'CHM' ||
    submission.submission_type === 'CHM-addon' ||
    submission.submission_type === 'Sedera' ||
    submission.submission_type === 'Sedera-addon' ||
    submission.submission_type === 'SMI' ||
    submission.submission_type === 'SMI-addon'
  ) {
    // Only expenses confirmed shareable belong in a sharing-ministry submission. Exclude both
    // 'not_shareable' (e.g. maintenance/OTC medications) and 'unknown' (unresolved). 'unknown'
    // only sits on incident-less pharmacy drafts today, so this is a no-op defensive guard that
    // keeps unknown treated consistently with the qualifying-amount sum in chooseSubmissionType.
    pendingExpenses = pendingExpenses.filter((e) =>
      e.shareability !== 'not_shareable' && e.shareability !== 'unknown')
  }

  return pendingExpenses
}

/**
 * Selects the expenses that belong in the given pending submission.
 *
 * Moved out of app/hooks/usePendingExpenses so that lib/todos/incident.ts can use
 * it without importing from app/hooks -- a lib -> app/hooks dependency becomes a
 * circular package dependency once the todo generators move into
 * @healthshare/shared (Phase 1 of docs/plans/embeddable-sdk.md) while the hooks
 * stay in apps/web. Despite living in a hooks file, this was never a hook: it is a
 * plain selector factory with no React involvement.
 *
 * app/hooks/usePendingExpenses re-exports it so existing imports keep resolving.
 */
export function selectPendingExpenses(submission: PendingSubmissionModel | UnsavedSubmissionModel) {
  return (s: RootState) => pendingExpensesFor(submission, {
    allMembershipExpenses: s.expenses.expenses,
    allMembershipSubmissions: s.submissions.submissions,
  })
}
